import { useState } from "react";
import { Alert, Box, Card, CircularProgress, Grid, Stack, Typography } from "@mui/material";

import { AutoComplete, DataLayout, MetaDrivenTable, StatusChip } from "../../../shared/components";
import MaterialsDashboard, { RegionCard } from "./MaterialsDashboard";
import { MetricTrendCard } from "./MetricTrendCard";

import { useFetchContractorDetails } from "../hooks/useFetchContractorDetails";
import { useFetchPerformanceTrend } from "../hooks/useFetchPerformanceTrend";
import { useFetchRegionalBreakdown } from "../hooks/useFetchRegionalBreakdown";
import { useFetchRiskAndWarning } from "../hooks/useFetchRiskAndWarning";
import { useFetchActivityList } from "../hooks/useFetchActivityList";

export const ContractorDetails = () => {

  const ActivitiesHeaders = [
    { alias_name: "rag", label: "RAG", display_order: 1, is_show: true },
    { alias_name: "activity_id", label: "ID", display_order: 2, is_show: true },
    { alias_name: "activity_name", label: "Activity Name", display_order: 3, is_show: true },
    { alias_name: "status_display_text", label: "Status", display_order: 4, is_show: true },
    { alias_name: "phase_name", label: "Phase", display_order: 5, is_show: true },
    { alias_name: "region_description", label: "Region", display_order: 6, is_show: true },
    { alias_name: "days_in_phase", label: "Days in Phase", display_order: 7, is_show: true },
    { alias_name: "query_count", label: "Queries", display_order: 8, is_show: true },
    { alias_name: "materials", label: "Materials", display_order: 9, is_show: true },
  ];

  const metrics = {
    rag: {
      type: "component",
      element: (value: { rag: string }) => (
        <Box
          sx={{
            width: 12,
            height: 12,
            bgcolor:
              value.rag === "amber"
                ? "warning.main"
                : value.rag === "green"
                  ? "success.main"
                  : "error.main",
            borderRadius: "50%",
          }}
        />
      ),
    },
    status_display_text: {
      type: "component",
      element: (value: { status_display_text: string }) => (
        <StatusChip
          label={value.status_display_text}
          color={
            value.status_display_text === "In Progress"
              ? "amber"
              : value.status_display_text === "Complete"
                ? "green"
                : "red"
          }
          variant="filled"
        />
      ),
    },
    query_count: {
      type: "component",
      element: (value: { query_count: number }) => (
        <StatusChip
          label={String(value.query_count)}
          color="blue"
          variant="filled"
        />
      ),
    },
    materials: {
      type: "component",
      element: (value: { materials: string }) => (
        <StatusChip
          label={String(value.materials) || "N/A"}
          color={
            value.materials === "Confirmed"
              ? "green"
              : value.materials === "Pending"
                ? "amber"
                : "red"
          }
          variant="filled"
        />
      ),
    },
  };

  const [selectedContractor, setSelectedContractor] = useState<string | null>(null);

  const { data: regionalBreakdownData, isLoading: isRegionalBreakdownLoading, isError: isRegionalBreakdownError, error: regionalBreakdownError } = useFetchRegionalBreakdown(selectedContractor);
  const { data: performanceTrendData, isLoading: isPerformanceTrendLoading, isError: isPerformanceTrendError, error: performanceTrendError } = useFetchPerformanceTrend(selectedContractor);
  const { data: riskAndWarningData, isLoading: isRiskAndWarningLoading, isError: isRiskAndWarningError, error: riskAndWarningError } = useFetchRiskAndWarning(selectedContractor);
  const { data: activityListData, isLoading: isActivityListLoading, isError: isActivityListError, error: activityListError } = useFetchActivityList(selectedContractor);
  const { data: contractorDetailsData, isLoading: isContractorDetailsLoading, isError: isContractorDetailsError, error: contractorDetailsError } = useFetchContractorDetails(selectedContractor);

  // const { data: deliverySnapshotData, isLoading: isDeliverySnapshotLoading, isError: isDeliverySnapshotError, error: deliverySnapshotError } = useFetchDeliverySnapshot(selectedContractor);
  // const { data: commercialViewData, isLoading: isCommercialViewLoading, isError: isCommercialViewError, error: commercialViewError } = useFetchCommercialView(selectedContractor);

  const headers = [
    { alias_name: "workstream", label: "Workstream", display_order: 1, is_show: true },
    { alias_name: "total_requests", label: "Total Requests", display_order: 1, is_show: true, style: { textAlign: 'end' } },
    { alias_name: "delays", label: "Delays", display_order: 1, is_show: true, style: { textAlign: 'end' } },
    { alias_name: "delay_rate_percentage", label: "Delay Rate Percentage", display_order: 1, is_show: true, style: { textAlign: 'end' } }
  ];

  const metrics2 = {
    total_requests: {
      type: "component",
      element: (value: { total_requests: number }) => (
        <Typography textAlign={'end'}>
          {value.total_requests}
        </Typography>
      ),
    },
    delays: {
      type: "component",
      element: (value: { delays: number }) => (
        <Typography textAlign={'end'}>
          {value.delays}
        </Typography>
      ),
    },
    delay_rate_percentage: {
      type: "component",
      element: (value: { delay_rate_percentage: number }) => (
        <Typography color={value.delay_rate_percentage > 50 ? 'error' : 'inherit'} textAlign={'end'}>
          {value.delay_rate_percentage}%
        </Typography>
      ),
    },
  };

  if (selectedContractor === null) {
    return (
      <Box sx={{ p: 2 }} display={'flex'} flexDirection={'column'} gap={2}>
        <Alert severity="info">Please select a contractor to view details.</Alert>
        <AutoComplete
          value={selectedContractor}
          onSelect={(value) => setSelectedContractor(value)}
          name={"contractor"}
          selectFirstByDefault={true}
        />
      </Box>
    );
  };

  if (isRegionalBreakdownLoading || isPerformanceTrendLoading || isRiskAndWarningLoading || isActivityListLoading || isContractorDetailsLoading) {
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        gap={2}
      >
        <CircularProgress />
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>

      <Stack spacing={2}>

        <AutoComplete
          value={selectedContractor}
          onSelect={(value) => setSelectedContractor(value)}
          name={"contractor"}
          selectFirstByDefault={true}
        />

        <DataLayout title="Regional / COAM Breakdown">
          <Box p={2}>
            {regionalBreakdownData?.length > 0 ? (
              <Grid container spacing={2}>
                {regionalBreakdownData.map((region: { description: string; on_time_delivery_percent: number; active_activities: number; rag_color: string }, index: number) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                    <RegionCard
                      region={region.description}
                      value={region.on_time_delivery_percent}
                      activities={region.active_activities}
                      color={region.rag_color}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" minHeight={100}>
                <Typography variant="body2" color="text.secondary">No regional data available</Typography>
              </Box>
            )}
          </Box>
        </DataLayout>

        <DataLayout title="Performance Trends (6 Months)">
          <Box p={2}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <MetricTrendCard
                  title="On time Delivery"
                  value={performanceTrendData.on_time_delivery.value + '%'}
                  data={[76, 75, 74, 73, 74, 75]}
                  color={performanceTrendData.on_time_delivery.rag === "green" ? "success" : performanceTrendData.on_time_delivery.rag === "red" ? "error" : "warning"}
                  trend="down"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <MetricTrendCard
                  title="Responsiveness"
                  value={performanceTrendData.responsiveness.value + '%'}
                  data={[76, 75, 74, 73, 74, 75]}
                  color={performanceTrendData.responsiveness.rag === "green" ? "success" : performanceTrendData.responsiveness.rag === "red" ? "error" : "warning"}
                  trend="down"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <MetricTrendCard
                  title="Quality Score"
                  value={performanceTrendData.quality_score.value + '%'}
                  data={[76, 75, 74, 73, 74, 75]}
                  color={performanceTrendData.quality_score.rag === "green" ? "success" : performanceTrendData.quality_score.rag === "red" ? "error" : "warning"}
                  trend="down"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <MetricTrendCard
                  title="Cost Variance"
                  value={performanceTrendData.cost_variance.value + '%'}
                  data={[76, 75, 74, 73, 74, 75]}
                  color={performanceTrendData.cost_variance.rag === "green" ? "success" : performanceTrendData.cost_variance.rag === "red" ? "error" : "warning"}
                  trend="down"
                />
              </Grid>
            </Grid>
          </Box>
        </DataLayout>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 7 }}>
            <DataLayout title="Materials & Resource Reliability" sx={{ height: "100%" }}>
              <MaterialsDashboard data={contractorDetailsData.materials_resource_reliability} />
              <MetaDrivenTable headers={headers} data={contractorDetailsData.patterns_by_workstream} dataMetrics={metrics2} sortDisabled small />
            </DataLayout>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <DataLayout title="Risk & Early Warnings" sx={{ height: "100%" }}>
              <Box sx={{ maxHeight: 350, overflowY: "auto" }}>
                {riskAndWarningData?.length > 0 ? (
                  <Stack spacing={0} divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}>
                    {riskAndWarningData.map((risk: { category_display_text: string; severity: string; status: string; activity_count: number; risk_description: string; action: string }, index: number) => (
                      <Box key={index} sx={{ px: 2, py: 1.5, "&:hover": { bgcolor: "action.hover" } }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.5}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <StatusChip
                              label={risk.severity}
                              color={
                                risk.severity?.toLowerCase() === "high" ? "red" :
                                risk.severity?.toLowerCase() === "medium" ? "amber" :
                                risk.severity?.toLowerCase() === "low" ? "green" : "default"
                              }
                              variant="filled"
                              size="small"
                            />
                            <Typography variant="subtitle2" fontWeight={600}>
                              {risk.category_display_text}
                            </Typography>
                          </Box>
                          {risk.activity_count > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {risk.activity_count} activities
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {risk.risk_description}
                        </Typography>
                        <Typography variant="caption" color="text.primary">
                          <strong>Action:</strong> {risk.action}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" minHeight={100}>
                    <Typography variant="body2" color="text.secondary">No risks or warnings found</Typography>
                  </Box>
                )}
              </Box>
            </DataLayout>
          </Grid>
        </Grid>

        <DataLayout title="Activity List">
          <MetaDrivenTable
            headers={ActivitiesHeaders}
            data={activityListData}
            dataMetrics={metrics}
            sortDisabled
          />
        </DataLayout>

      </Stack>

    </Box>
  );

};